import React, { useState } from 'react';
import {
  EllipsisVerticalIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { BottomSheet } from '../ui/BottomSheet';
import PermissionGate from '../PermissionGate';
import {
  avatarTone,
  formatPlanPrice,
  planActiveChipClass,
  planInitials,
  planTypeChipClass,
  planTypeLabel,
} from './planTemplateUtils';

export interface PlanTemplateCardData {
  id: number;
  name: string;
  planType: string;
  basePrice: number;
  maxFamilyMembers: number;
  features: string[];
  isActive: boolean;
}

export interface PlanTemplateMobileCardProps {
  template: PlanTemplateCardData;
  onOpenDetail: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const PlanTemplateMobileCard: React.FC<PlanTemplateMobileCardProps> = ({
  template,
  onOpenDetail,
  onEdit,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const items = template.features?.length ?? 0;

  const run = (fn: () => void) => {
    setMenuOpen(false);
    fn();
  };

  return (
    <>
      <article
        className={clsx(
          'rounded-2xl border border-[#E5EAF1] bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]',
          'transition duration-150 active:scale-[0.99] active:bg-slate-50/80'
        )}
      >
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onOpenDetail}
            className="flex min-w-0 flex-1 items-start gap-3 text-left"
          >
            <div
              className={clsx(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ring-1 ring-inset',
                avatarTone(`${template.name}${template.id}`)
              )}
            >
              {planInitials(template.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="truncate text-[16px] font-semibold leading-snug text-slate-900">
                  {template.name}
                </p>
                <span className={clsx('shrink-0', planActiveChipClass(template.isActive))}>
                  {template.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <span
                className={clsx(
                  'mt-1.5 inline-flex rounded-lg px-2 py-0.5 text-[11px] font-semibold',
                  planTypeChipClass(template.planType)
                )}
              >
                {planTypeLabel(template.planType)}
              </span>
              <div className="mt-3 grid grid-cols-3 gap-2 border-t border-[#E5EAF1] pt-2.5">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Precio base
                  </p>
                  <p className="mt-0.5 text-xs font-semibold tabular-nums text-slate-900">
                    {formatPlanPrice(template.basePrice)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Máx. familiares
                  </p>
                  <p className="mt-0.5 text-xs font-semibold tabular-nums text-slate-900">
                    {template.maxFamilyMembers}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Ítems
                  </p>
                  <p className="mt-0.5 text-xs font-semibold tabular-nums text-slate-900">{items}</p>
                </div>
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(true);
            }}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 active:scale-95"
            aria-label="Más acciones"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
        </div>
      </article>

      <BottomSheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Acciones">
        <div className="space-y-0.5 pb-2">
          <button
            type="button"
            onClick={() => run(onOpenDetail)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 transition hover:bg-slate-50 active:scale-[0.99]"
          >
            <EyeIcon className="h-5 w-5 text-slate-400" />
            Ver detalles
          </button>
          <PermissionGate permission="plan_templates.update">
            <button
              type="button"
              onClick={() => run(onEdit)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 transition hover:bg-slate-50 active:scale-[0.99]"
            >
              <PencilIcon className="h-5 w-5 text-slate-400" />
              Editar
            </button>
          </PermissionGate>
          <PermissionGate permission="plan_templates.delete">
            <div className="my-2 border-t border-slate-100" />
            <button
              type="button"
              onClick={() => run(onDelete)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-rose-600 transition hover:bg-rose-50 active:scale-[0.99]"
            >
              <TrashIcon className="h-5 w-5 text-rose-500" />
              Eliminar
            </button>
          </PermissionGate>
        </div>
      </BottomSheet>
    </>
  );
};

export default PlanTemplateMobileCard;
